import { AxiosInstance } from 'axios'
import countries, { Alpha3Code } from 'i18n-iso-countries'

import { unpayableOrganisationReasons } from '@percent/admin-dashboard/app/organizations/organizationsDetail/components/overviewDetails/paymentDetails/MarkOrganisationAsUnpayableModal'
import { orgSubActivities } from '@percent/admin-dashboard/constants/organisationActivities'
import { CurrencyCode } from '@percent/utility'
import { OrganisationRelationship, OrganisationTypes } from '@percent/domain/verifications'
import { CountrySpecificData } from '@percent/admin-dashboard/app/validations/nonprofitDetail/dialogs/registryModal/RegistryModal.types'

export const dgrStatuses = {
  AUS_DGR1: 'aus-dgr1',
  AUS_DGR2: 'aus-dgr2',
  AUS_DGR4: 'aus-dgr4'
} as const

export type DgrStatus = typeof dgrStatuses[keyof typeof dgrStatuses]

export type Organisation = {
  id: string
  name: string
  email: string | null
  displayName: string | null
  donated: number
  description: string | null
  countryCode: Alpha3Code
  milestones: milestone[]
  categories: categories[]
  type: OrganisationTypes
  logo: string | null
  images: string[]
  registry: string
  registryName: string
  registryId: string
  website: string | null
  address: string
  addressLine1: string | null
  addressLine2: string | null
  city: string | null
  state: string | null
  postcode: string | null
  country: string | null
  registrationDate: string | null
  registrationActive: boolean
  currencyCode: string
  trustees: string | null
  claimedAt: string | null
  createdAt: string
  registryData?: RegistryData
  relationships?: OrganisationRelationship[]
  document?: string
  phone?: string | null
  fieldConstraints?: fieldConstraintsType
  types?: Partial<{ [key in OrganisationTypes]: 'yes' | 'no' | 'unknown' }>
  countrySpecificProperties?: {
    fcra?: string
    DGR1?: boolean
    DGR2?: boolean
    DGR4?: boolean
  }
}

export type PaymentMethod = {
  bankAccountDetails?: {
    holderName: string
    version: string
    wireTransferDetails: {
      accountNumber: string
      bankCode: string
      type: string
    }
  }
  method: 'bank_transfer' | 'card' | 'cheque' | 'unable_to_pay'
  paymentUrl?: string
  reason?: string
  createdAt: string
  createdBy: {
    id: string
    email: string
    fullName?: string
  }
}

type RegistryData = { name: string }

export type categories = {
  id: string
  name: string
  imageUrl: string
  createdAt: string
}

export type milestone = {
  id: number
  amount: number
  reward: string
  currencyCode: string
  organisationId: string
}

export type adminServiceCapabilities = {
  percentClient: AxiosInstance
}

export type Claim = {
  acceptedAt: string | null
  account: {
    email: string
    fullName: string
    id: string
    phoneNumber: string
  }
  accountId: string
  createdAt: string
  deletedAt: string | null
  description: string | null
  id: string
  image: string | null
  organisation: {
    id: string
    name: string
    countryCode: countries.Alpha3Code
  }
  organisationId: string
  rejectedAt: string | null
  validationSubmissionId: string | null
}

export type GetValidationsRequestProps = {
  status?: string | null
  sort?: string | null
  direction?: string | null
  pageSize?: 0
}

export type AcceptValidationRequestProps = {
  registryId: string
}

export type ValidationDocument = {
  id: string
  validationRequestId: string
  url: string
  createdAt: string
  deletedAt: string
}

export type ValidationSubmissionDocumentType = 'validation_request' | 'agent_verification'

export enum VSDocumentLLMResultKind {
  AVDocumentContentReview = 'av_document_content_review'
}

export enum DocumentValidity {
  VALID = 'Valid',
  EXPIRED = 'Expired',
  UNCLEAR = 'Unclear'
}

export enum MatchStatus {
  EXACT_MATCH = 'Exact Match',
  PARTIAL_MATCH = 'Partial Match',
  NO_MATCH = 'No Match',
  ALLOWED_ORGANIZATION = 'Allowed Organization'
}

export enum RoleValidity {
  ACCEPTABLE = 'Acceptable',
  NOT_ACCEPTABLE = 'Not Acceptable',
  UNCLEAR = 'Unclear'
}

export type RoleCategory =
  | 'teaching'
  | 'pedagogical'
  | 'support'
  | 'management'
  | 'peripheral_admin'
  | 'general_staff'
  | 'inconclusive'
  | 'none'
  | 'students'

export type CheckOutcome = 'accepted' | 'rejected' | 'inconclusive'

export const DOCUMENT_TYPE_CATEGORIES = ['teaching_staff', 'student', 'invalid'] as const

export type DocumentTypeCategory = typeof DOCUMENT_TYPE_CATEGORIES[number]

export const NAME_MATCH_STATUSES = ['full_match', 'partial_match', 'no_match', 'inconclusive'] as const

export type NameMatchCategory = typeof NAME_MATCH_STATUSES[number]

export const ORG_MATCH_STATUSES = ['org_match', 'whitelist_match', 'no_match', 'inconclusive'] as const

export type OrgMatchCategory = typeof ORG_MATCH_STATUSES[number]

export type RoleCategoryVerdict = 'accepted' | 'rejected'

export type RoleCategoryVerdicts = Partial<Record<RoleCategory, RoleCategoryVerdict>>

export const markDocAsFraudReasons = [
  'altered_document',
  'suspicious_missing_data',
  'suspicious_metadata',
  'ai_generated',
  'fabricated_document',
  'publicly_available_document'
] as const

export type DocumentFraudCode = typeof markDocAsFraudReasons[number]

export type ValidationSubmissionDocument = {
  id: string
  validationInviteId: string
  url: string
  createdAt: string
  validationSubmissionId: string
  type?: ValidationSubmissionDocumentType
  firstNameFound?: boolean | null
  lastNameFound?: boolean | null
  organisationNameFound?: boolean | null
  markedAsFraudAt?: string | null
  fraudCode?: DocumentFraudCode | null
  translation?: string | null
  personalNames?: string[]
  organizationNames?: string[]
  roles?: string[]
  documentType?: string
  documentValidity?: DocumentValidity
  confidence?: string
  documentMatchingResults?: {
    nameMatchStatus?: MatchStatus
    organizationMatchStatus?: MatchStatus
    roleValidity?: RoleValidity
    nameMatchOutcome?: CheckOutcome | null
    nameMatchCategory?: NameMatchCategory | null
    orgMatchCategory?: OrgMatchCategory | null
    orgMatchOutcome?: CheckOutcome | null
    documentTypeCategory?: DocumentTypeCategory | null
    documentTypeOutcome?: CheckOutcome | null
    roleCategories?: RoleCategory[]
    roleCategoryOutcome?: CheckOutcome | null
    roleCategoryVerdicts?: RoleCategoryVerdicts | null
  }
  issueDate?: string | null
}

export type fieldConstraintsType = {
  fields: {
    registryId: {
      required: boolean
      regex?: string
    }
  }
}

export type ValidationAutomationDecision =
  | { decision: 'accept' | 'skip' }
  | { decision: 'reject'; rejectionReasonCode: string }
  | { decision: 'merge'; organisationId: string }

export type ValidationAutomationDocument = {
  documentKey: string
  documentType: string
  organisationName: string | null
  registryId: string | null
  issuingAuthority: string | null
  date: string | null
  nameMatchesSubmission: boolean
  registryIdMatchesSubmission: boolean
  findings: string | null
}

export type ValidationAutomation = {
  id: string
  response: {
    decision: ValidationAutomationDecision
    reasoning: string
    summary: string
    documentAnalysis?: {
      summary: string
      documentsReviewed?: number
      documents?: ValidationAutomationDocument[]
    }
  }
  createdAt: string
}

export type ValidationAutomationQcVerdict =
  | { decision: 'approve' }
  | { decision: 'reject'; rejectionReasonCode: string }
  | { decision: 'merge'; organisationId: string }

export type ValidationAutomationQc = {
  id: string
  verdict: ValidationAutomationQcVerdict
  reasoning: string | null
  submittedBy: string
  createdAt: string
}

export type Validation = {
  id: string
  website: string
  email: string
  name: string
  registryName?: string
  registryId?: string
  addressLine1: string
  addressLine2: string
  city: string
  postal: string
  state: string
  countryCode: Alpha3Code
  createdAt: string | null
  deletedAt: string | null
  acceptedAt: string | null
  rejectedAt: string | null
  documents: ValidationDocument[]
  validationSubmissionId: string | null
  partner: {
    id: string
    name: string
  }
  reviewedBy: {
    account: string
    accountEmail: string
  } | null
  configuration: {
    name: string | null
    allowedOrganisations?: {
      types?: OrganisationTypes[]
    }
  }
  organisation?: Pick<Organisation, 'name' | 'registry' | 'registryId' | 'registryData' | 'relationships'> & {
    countrySpecificData?: CountrySpecificData
  }
  organisationId?: string
  organisationTypes?: OrganisationTypes[]
  organisationSubTags?: { id: string; meetsCriteria: boolean }[] | null
  fieldConstraints?: fieldConstraintsType
  foundEmails: { email: string; pageUrl: string }[] | null
  verifiedWebsite?: string | null
  websiteInvalidReason?: WebsiteInvalidReason | null
  websiteVerifiedAt?: string | null
  websiteVerifiedBy?: {
    account: string
    accountEmail: string
  } | null
  automation?: ValidationAutomation | null
  automationQc?: ValidationAutomationQc | null
}

export type AdminServiceGetPartnerDeveloperListProps = {
  page?: number
  pageSize?: number
  partnerId: string
  filter?: string
}

export type AdminServiceGetPartnerScopeListProps = {
  page?: number
  pageSize?: number
  partnerId: string
}

export type AdminServiceGetRelatedOrganisationsListProps = {
  page?: number
  pageSize?: number
  registry?: string
  registryId?: string
  relationshipType?: string
}

export type AdminServiceGetSimilarOrganisationsListProps = {
  registry?: string
  registryId?: string
  name?: string
  countryCode?: Alpha3Code
  city?: string
  website?: string
  address?: string
  page?: number
  pageSize?: number
}

export type AdminServiceGetValidationSubmissionDocumentsProps = {
  page?: number
  pageSize?: number
  validationSubmissionId?: string | null
}

export type AdminServiceRequestMarkDocumentAsFraudProps = {
  id: string
  payload: {
    fraudCode: DocumentFraudCode
  }
}

export type AdminServiceGetPartnerListProps = {
  pageSize?: number
  query?: string
  partnerIds?: string[]
}

export enum VettingStatus {
  CLEAR = 'clear',
  PENDING = 'pending',
  FLAG = 'flag',
  YES = 'yes',
  NO = 'no',
  PASS = 'pass',
  FAIL = 'fail',
  PENDING_REVIEW = 'pending_review'
}

export enum YesNo {
  YES = 'yes',
  NO = 'no'
}

export type ComplianceEventType =
  | 'commercial'
  | 'controversial'
  | 'hateSpeech'
  | 'compliance'
  | 'adverseMedia'
  | 'sanction'
  | 'warning'

export type VettingEvent = {
  id: string
  organisationId: string
  scope: ComplianceEventType
  result?: {
    status: YesNo
  }
  status: VettingStatus
  createdBy: string | null
  expiresAt: string | null
  createdAt: string
  deletedAt: string | null
  account: {
    id: string
    fullName: string | null
    email: string
  } | null
}

export type AutomatedVettingEventResults = {
  results?: Record<
    string,
    {
      status: VettingStatus
    }
  >
  createdAt?: string
  complyAdvantageSearchId?: string
}

export type QueryParams = {
  status?: string
  completed?: string
  accepted?: string
  rejected?: string
}

export type CursorPaginationData<T> = {
  data: [T]
  object: string
  totalResults: number
  pageNumber: number
  pageSize: number
  _links: {
    current: string
    next: string
    prev: string
  }
}

export type PatchOrganisationParams = {
  payload: {
    displayName?: string | null
    description?: string | null
    registrationActive?: boolean
    confirmGoogleDeactivation?: boolean
    website?: string | null
    email?: string | null
    registry?: string
    registryId?: string
    name?: string
    phone?: string | null
    addressLine1?: string | null
    addressLine2?: string | null
    city?: string | null
    state?: string | null
    postcode?: string | null
    country?: string | null
    logo?: string | null | FormData
    postal?: string | null
    countrySpecificData?: {
      fcra?: string | null
      dgrStatusList?: DgrStatus[] | null
    }
  }
  id: string
}

export type UnableToPayReasons = typeof unpayableOrganisationReasons[number]

export type UpdateOrganisationPaymentMethodParams = {
  payload:
    | { method: 'card'; paymentUrl: string }
    | { method: 'cheque' }
    | { method: 'unable_to_pay'; reason: UnableToPayReasons }
  id: string
}

export type FetchOrganisationsParams = {
  query?: string | null
  countryCode?: string | null
  onlyActive?: boolean
  page?: number
  pageSize?: number
  organisationIds?: string[]
  type?: OrganisationTypes[]
}

export type claims = {
  id: string
  accountId: string
  organisationId: string
  image: string
  description: string
  createdAt: string
  deletedAt: string
  acceptedAt: string
  rejectedAt: string
  organisation: {
    id: string
    name: string
    countryCode: Alpha3Code
  }
  account: {
    id: string
    fullName: string
    email: string
    phoneNumber: string
  }
}

export type FetchClaimsParams = {
  accepted?: boolean
  rejected?: boolean
  pageSize?: number
}

export type FetchRegistriesParams = {
  countryCode: string
}

export type FetchVettingEventsParams = {
  organisationId?: string
  pageSize?: number
}

export type Partner = {
  createdAt: string
  currencyCode: string
  deletedAt: string
  id: string
  image: string | null
  name: string
  publicKey: string
  allowedActivitySubTags: string[] | null
}

export type Subsidiary = {
  id: string
  name: string
  currencyCode: CurrencyCode
  location?: string
}

export enum AgentVerificationStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  PENDING_REVIEW = 'pending_review',
  PENDING_USER_VERIFICATION = 'pending_user_verification',
  PERCENT_ACTIONABLE = 'percent_actionable',
  EXTERNAL_ACTIONABLE = 'external_actionable'
}

export enum AgentVerificationState {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  PERCENT_ACTIONABLE = 'percent_actionable',
  EXTERNAL_ACTIONABLE = 'external_actionable'
}

export enum ActivityCheckStatus {
  PENDING_VALIDATION_REQUEST = 'pending_validation_request',
  VALIDATION_REQUEST_FAILED = 'validation_request_failed',
  PENDING = 'pending',
  LIVE = 'live',
  CANCELLED = 'cancelled'
}

export type ValidationRequest = {
  acceptedAt: string | null
  addressLine1: string | null
  addressLine2: string | null
  city: string | null
  countryCode: string
  createdAt: string
  deletedAt: string | null
  email: string | null
  id: string
  name: string
  organisationId: string | null
  partnerId: string
  postal: string | null
  registryId: string
  registryName: string
  rejectedAt: string | null
  rejectionReason: string | null
  state: string | null
  website: string | null
  usaGroupExempt: string | null
}

type OrganisationActivity = {
  id: string
  createdAt: string
  createdBy: string
  activity: keyof typeof orgSubActivities
}

export type ActivityCheck = {
  activity: keyof typeof orgSubActivities
  createdAt: string | null
  countryCode: Alpha3Code
  id: string
  missionStatement: string | null
  organisationActivity: OrganisationActivity | null
  organisationId: string | null
  partnerId: string | null
  partnerName: string | null
  status: ActivityCheckStatus
  validationRequest?: ValidationRequest
  organisation: {
    id: string | null
    name: string | null
  }
  validationRequestId: string
}

export type ActivityCheckById = ActivityCheck & {
  organisationActivity: (OrganisationActivity & { email: string }) | null
}

export type FetchMonitoringParams = {
  status?: string
  pageSize?: number
  sort?: string | null
  direction?: string | null
}

export type MonitoringList = {
  id: string
  countryCode: Alpha3Code
  organisationId: string
  organisationName: string
  results:
    | {
        warning: string
        sanction: string
        hateSpeech: string
        compliance: string
        commercial: string
        adverseMedia: string
        controversial: string
      }
    | {}
  createdAt: string
  partnerName: string | null
  partnerId: string
}

export type PatchValidationRequestParams = {
  payload: {
    website?: string | null
    email?: string | null
    verifiedWebsite?: string
  }
  id: string
}

export const websiteInvalidReasons = ['not_opening', 'wrong_website', 'fraudulent', 'undeveloped_website'] as const
export type WebsiteInvalidReason = typeof websiteInvalidReasons[number]

export type ReviewValidationRequestWebsiteParams = {
  id: string
  payload:
    | { verifiedWebsite: string; invalidReason: null }
    | { verifiedWebsite: string | null; invalidReason: WebsiteInvalidReason }
}

export enum RejectionReasonsCodeType {
  NotEligible = 'not_eligible',
  IncorrectDocumentation = 'incorrect_documentation',
  NotAttributableToRegistry = 'not_attributable_to_registry',
  SuspiciousApplication = 'suspicious_application',
  Other = 'other'
}

export type PostRejectValidationRequest = {
  rejectionReasonCode: RejectionReasonsCodeType | string
}

export type VrQcVerdict =
  | { decision: 'accept' }
  | { decision: 'reject'; rejectionReasonCode: RejectionReasonsCodeType | string }
  | { decision: 'merge'; organisationId: string }

export type SubmitValidationRequestQcParams = {
  id: string
  payload: {
    verdict: VrQcVerdict
    reasoning?: string | null
  }
}

export type PartnerScope = {
  createdAt: string
  deletedAt: string | null
  id: string
  partnerId: string
  scopeName: string
}

export type SimilarOrganisation = {
  id: string
  name: string
  organisationTypes: OrganisationTypes[]
  website?: string
  registrationActive?: boolean | null
  countryCode: Alpha3Code
  registry: string
  registryId: string
  address: {
    addressLine1?: string
    addressLine2?: string
    city?: string
    postal?: string
    state?: string
  }
}

export type PostOrganisationLogoProps = {
  payload: {
    formData: object
  }
  organisationId?: string
}

export type OrganisationLogoBody = {
  url: string
}

export type CreateOrganizationProps = {
  payload: {
    formData: object
    file?: File
  }
}

export type GetSignedUrlForDocumentProps = {
  documentUrl: string
}

export type GetSignedUrlForDocumentBody = {
  signedUrl: string
  documentId: string
}

export type VettingEventScope = 'sanction' | 'warning' | 'adverseMedia' | 'hateSpeech' | 'controversial' | 'commercial'

export type SubmitVettingEventsPayload = {
  flags: {
    scope: VettingEventScope
    status: string
  }[]
}

export type OrganisationAddressRow = {
  organisationId: string
  address: string
  postalCode?: string
  city: string
  state: string
}

export type OrganisationsBatchUpdateProps = {
  payload: OrganisationAddressRow[]
}

export type OrganisationDefaultLocale = {
  defaultLocale: string | null
  organisationId: string
}

export type AvailableLocales = {
  availableLocales: string[]
}

export type OrganisationFiles = {
  id: string
  organisationId: string
  description: string
  url: string
  dimensions: {
    width: number
    height: number
    aspectRatio: string
  }
}

export type UploadOrganisationFilesProps = {
  payload: {
    formData: object
  }
  id: string
}
export type StaticLink = {
  id: string
  slug: string
  validationSubmissionConfigurationId: string
  redirectUrl: string
  isEnabled: boolean
  enablePost: boolean
  allowedOrigin: string
  metadataTemplate: Record<string, unknown> | null
  createdAt: string
}

export type CreateStaticLinkPayload = {
  slug: string
  validationSubmissionConfigurationId: string
  redirectUrl?: string | null
  isEnabled?: boolean
  enablePost?: boolean
  allowedOrigin?: string | null
}

export type UpdateStaticLinkPayload = Partial<CreateStaticLinkPayload>

export type PartnerConfigRecord<T = Record<string, unknown>> = {
  id: string
  partnerId: string
  key: string
  value: T
  createdAt: string
}

// Giving and Donation transactional email configs use string 'enabled'|'disabled' rather than boolean.
// This is an intentional backend API inconsistency — do not normalise these to boolean.
// The [key: string]: unknown index signature is required so spread writes (PUT with full record)
// preserve unknown future fields returned by the API without TypeScript narrowing them away.
export type GivingFeaturesConfigValue = {
  profileSecondaryEmails: 'enabled' | 'disabled'
  opportunityTimeLogsRewardsEligibility: 'enabled' | 'disabled'
  [key: string]: unknown
}

export type GivingTransactionalEmailsConfigValue = {
  welcomeEmail: 'enabled' | 'disabled'
  [key: string]: unknown
}

export type DonationGiftNoteConfigValue = {
  enabled: boolean
  [key: string]: unknown
}

export type DonationTransactionalEmailsConfigValue = {
  walletFundsEmail: 'enabled' | 'disabled'
  [key: string]: unknown
}

export type FundraisingPublicFundraisersConfigValue = {
  enabled: boolean
  [key: string]: unknown
}

export type DonationMatchingFamilialConfigValue = {
  enabled: boolean
  [key: string]: unknown
}

export type DonationMatchingDirectToCampaignConfigValue = {
  enabled: boolean
  [key: string]: unknown
}

export type DonationMatchingDonationDateEligibilityConfigValue = {
  enabled: boolean
  source?: { type: string }
  [key: string]: unknown
}

// Disjoint per-module payloads so the compiler rejects a cross-module key; the key→value pairing is enforced by the typed save-handler wrappers in usePartnerGivingConfigs, not here.
// Limited to the giving keys the UI actually writes (V1 toggles + V3 editable rows); read-only keys
// (supported_currencies, regions, preferred_countries, spotlighted_orgs, featured) are deliberately excluded.
export type UpdateGivingConfigPayload = {
  partnerId: string
  key:
    | 'features'
    | 'transactional_emails'
    | 'theme'
    | 'home_page_banner_content'
    | 'calendar_integration'
    | 'help_link'
    | 'require_organisation_time_log'
  value:
    | GivingFeaturesConfigValue
    | GivingTransactionalEmailsConfigValue
    | GivingThemeConfigValue
    | GivingHomePageBannerContentConfigValue
    | string[]
    | GivingHelpLinkConfigValue
    | boolean
}

export type UpdateDonationConfigPayload = {
  partnerId: string
  key: 'gift_note' | 'transactional_emails' | 'restricted_countries_for_donation'
  value: DonationGiftNoteConfigValue | DonationTransactionalEmailsConfigValue | DonationRestrictedCountriesConfigValue
}

export type UpdateFundraisingConfigPayload = {
  partnerId: string
  key: 'public_fundraisers'
  value: FundraisingPublicFundraisersConfigValue
}

export type UpdateDonationMatchingConfigPayload = {
  partnerId: string
  key: 'familial_donation_matching' | 'direct_to_campaign_match_requests'
  value: DonationMatchingFamilialConfigValue | DonationMatchingDirectToCampaignConfigValue
}

export type GivingThemeColours = {
  primary: string
  primaryHover: string
  primaryActive: string
  primaryFocus: string
  primary100: string
  primary400: string
  primary600: string
  info30: string
  info100: string
  info400: string
}

export type GivingThemeConfigValue = {
  colours: GivingThemeColours
  overlayGradientPercentage: number
}

export type GivingHomePageBannerContentConfigValue = {
  welcomeCopy: boolean
  searchBar: boolean
  // Optional (omitted) when unset — the backend validator is z.string().optional(), which rejects null.
  welcomeCopyTitle?: string
  welcomeCopySubtitle?: string
  welcomeCopyColor?: string
}

export type GivingRegion = {
  id: string
  name: string
  countryCodes: string[]
}

export type GivingRegionsConfigValue = Record<string, GivingRegion>

export type GivingHelpLinkConfigValue = {
  link: string
  title: string
}

export type GivingDatePeriodConfigurationValue = {
  date: string
  period: 'year'
}

export type VolunteeringAllowanceConfigValue = {
  renewsAt: string
  allowanceMinutes: number
  periodicity: 'annual' | 'quarterly'
}

export type GivingSpotlightedOrgsConfigValue = {
  organisationIds: string[]
  partnerLogo: string
}

type GivingFeaturedOrganisation = {
  kind: 'featured_organisation'
  organisationId: string
  title: string
  description: string
  image?: string
  country?: string
}

type GivingFeaturedFundraiser = {
  kind: 'featured_fundraiser'
  fundraiserId: string
  description?: string
  country?: string
}

type GivingFeaturedOpportunity = {
  kind: 'featured_opportunity'
  opportunityId: string
  description?: string
  country?: string
}

export type GivingFeaturedItem = GivingFeaturedOrganisation | GivingFeaturedFundraiser | GivingFeaturedOpportunity

export type GivingFeaturedConfigValue = GivingFeaturedItem[]

// `: readonly FeaturedKind[]` ties this list to the GivingFeaturedItem union so a renamed kind or a typo'd entry is a compile error.
export type FeaturedKind = GivingFeaturedItem['kind']

export const FEATURED_KINDS: readonly FeaturedKind[] = [
  'featured_organisation',
  'featured_fundraiser',
  'featured_opportunity'
]

export type DonationBlockedRegion = {
  countryCode: string
  state?: string
}

export type DonationRestrictedCountriesConfigValue = {
  blockedRegions: DonationBlockedRegion[]
}

export enum PartnerName {
  GOOGLE = 'google',
  CANVA = 'canva',
  MICROSOFT = 'microsoft',
  GOODSTACK = 'goodstack',
  GOODSTACK_ADMIN = 'percent admin dashboard users',
  GOODSTACK_OUTSOURCED = 'goodstack outsourced'
}

export const validationRequestLogTypes = [
  'document_requested',
  'document_received',
  'validation_request_accepted',
  'validation_request_rejected'
] as const

export type ValidationRequestLogType = typeof validationRequestLogTypes[number]

export type ValidationRequestLog<T extends Record<string, unknown> = Record<string, unknown>> = {
  id: string
  type: ValidationRequestLogType
  createdAt: string
  data?: T
}

export type ValidationRequestLogsResponse = ValidationRequestLog[]
